import { Box, Button, Chip, TextField, Typography } from "@mui/material";
import { ROW_BTN_SX, SECTION_SUBTITLE_SX } from "./gate2Tokens";
import {
  SCOPE_CARD_HEADER_ROW_SX,
  SCOPE_CARD_TITLE_SX,
  SCOPE_METRICS_CARD_SX,
} from "./gate2ScopeTableStyles";
import {
  apiConnectionSummaryRows,
  apiCredentialField,
  authTypeNeedsCredentials,
} from "./gate2ApiForm";
import Gate2MissingConnectorBanner from "./Gate2MissingConnectorBanner";

const labelSx = {
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "20px",
  color: "#707070",
  mb: 0.5,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    fontFamily: "Lato, sans-serif",
    fontSize: 14,
    boxShadow: "inset 0px 2px 3px 1px rgba(85, 85, 85, 0.2)",
    "& fieldset": { borderColor: "#707070" },
    "&:hover fieldset": { borderColor: "#707070" },
    "&.Mui-focused fieldset": { borderColor: "#707070", borderWidth: "1px" },
  },
};

function formatVerifiedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function ApiConnectionCard({
  form,
  credentials,
  onFormChange,
  onCredentialsChange,
  disabled = false,
  gate1Source = null,
  onOpenGate1,
}) {
  const declared = Boolean(gate1Source?.available);
  const authKind = String(form.authType || "").toLowerCase();
  const isBearer = authKind.includes("bearer");
  const credentialField = isBearer ? null : apiCredentialField(form.authType);
  const needsBasicEmail = authKind.includes("basic");
  const needsApiKeyHeader = authKind.includes("api key");

  return (
    <Box sx={SCOPE_METRICS_CARD_SX}>
      <Box sx={SCOPE_CARD_HEADER_ROW_SX}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={SCOPE_CARD_TITLE_SX}>API connection</Typography>
          <Typography sx={{ ...SECTION_SUBTITLE_SX, color: "#475569" }}>
            Using the API surface declared in Gate 1 — System & Evaluation Readiness. Each test case
            prompt is sent to this endpoint.
          </Typography>
        </Box>
        {declared ? (
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0, flexWrap: "wrap" }}>
            <Button variant="outlined" disableElevation disabled={disabled} onClick={onOpenGate1} sx={ROW_BTN_SX}>
              Edit connection details
            </Button>
          </Box>
        ) : null}
      </Box>

      {declared ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              p: 2,
              borderRadius: "8px",
              bgcolor: gate1Source.ready ? "#F0FDF4" : "#EFF6FF",
              border: `1px solid ${gate1Source.ready ? "#BBF7D0" : "#BFDBFE"}`,
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Chip
              size="small"
              label={gate1Source.ready ? "Verified in Gate 1" : "Declared in Gate 1"}
              sx={{
                height: 20,
                fontFamily: "Lato, sans-serif",
                fontSize: 11,
                fontWeight: 700,
                bgcolor: gate1Source.ready ? "#DCFCE7" : "#DBEAFE",
                color: gate1Source.ready ? "#15803D" : "#1D4ED8",
              }}
            />
            <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
              {form.baseUrl || "—"}
              {form.endpoint ? ` ${form.method || "POST"} ${form.endpoint}` : ""}
            </Typography>
            {formatVerifiedAt(gate1Source.verifiedAt) ? (
              <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 12, color: "#64748B" }}>
                {formatVerifiedAt(gate1Source.verifiedAt)}
              </Typography>
            ) : null}
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 1.5,
              width: "100%",
            }}
          >
            {apiConnectionSummaryRows(form).map((row) => (
              <Box key={row.key}>
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: "#94A3B8",
                  }}
                >
                  {row.label}
                </Typography>
                <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#383838" }}>
                  {String(row.value)}
                </Typography>
              </Box>
            ))}
          </Box>
          {!form.endpoint && onFormChange ? (
            <Box sx={{ maxWidth: 360, width: "100%" }}>
              <Typography sx={labelSx}>
                <Box component="span" sx={{ color: "#DE350B" }}>
                  *
                </Box>
                Endpoint path
              </Typography>
              <TextField
                fullWidth
                size="small"
                disabled={disabled}
                placeholder="/ask"
                value={form.endpoint || ""}
                onChange={(e) => onFormChange({ ...form, endpoint: e.target.value })}
                sx={fieldSx}
              />
            </Box>
          ) : null}
          {isBearer ? (
            gate1Source?.bearerTokenSaved ? (
              <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#64748B" }}>
                The bearer token saved in System Readiness will be used for this run.
              </Typography>
            ) : (
              <Gate2MissingConnectorBanner
                disabled={disabled}
                onOpenGate1={onOpenGate1}
                message="The bearer token from System Readiness is missing. Update it there before running metrics."
              />
            )
          ) : authTypeNeedsCredentials(form.authType) && credentialField ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 2,
                width: "100%",
              }}
            >
              {needsBasicEmail ? (
                <Box>
                  <Typography sx={labelSx}>
                    <Box component="span" sx={{ color: "#DE350B" }}>*</Box>
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={disabled}
                    placeholder="user@example.com"
                    value={credentials.basicEmail || ""}
                    onChange={(e) => onCredentialsChange({ ...credentials, basicEmail: e.target.value })}
                    sx={fieldSx}
                  />
                </Box>
              ) : null}
              {needsApiKeyHeader ? (
                <Box>
                  <Typography sx={labelSx}>API key header</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    disabled={disabled}
                    placeholder="X-API-Key"
                    value={credentials.apiKeyHeader || ""}
                    onChange={(e) => onCredentialsChange({ ...credentials, apiKeyHeader: e.target.value })}
                    sx={fieldSx}
                  />
                </Box>
              ) : null}
              <Box>
                <Typography sx={labelSx}>
                  <Box component="span" sx={{ color: "#DE350B" }}>
                    *
                  </Box>
                  {credentialField.label}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  autoComplete="new-password"
                  disabled={disabled}
                  placeholder={credentialField.placeholder}
                  value={credentials[credentialField.key] ?? ""}
                  onChange={(e) =>
                    onCredentialsChange({ ...credentials, [credentialField.key]: e.target.value })
                  }
                  sx={fieldSx}
                />
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontFamily: "Lato, sans-serif", fontSize: 13, color: "#64748B" }}>
              Auth type is {form.authType || "not set"} — no credential is required for this run.
            </Typography>
          )}
        </>
      ) : (
        <Gate2MissingConnectorBanner
          disabled={disabled}
          onOpenGate1={onOpenGate1}
          message={
            gate1Source?.message ||
            "No API connection has been verified yet. Run the system check in Gate 1 — System & Evaluation Readiness first."
          }
        />
      )}
    </Box>
  );
}
